// @greenlight-spec: 11-observability.md#zombie-app-detection
// @greenlight-spec: 28-it-dashboard-ui.md
// The owner's answer to an inactivity notice, shared by the app-detail power
// menu and the banner above it so both drive the same endpoint.
import * as React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';

import { api } from '@/lib/api';
import { mutationErrorMessage } from '@/lib/apiErrors';
import { refreshAfterLifecycleChange } from './lifecycleQueries';

export type ActivityAction = 'extend';

export interface AppActivityActions {
  /** Which request is in flight, or null. */
  pending: ActivityAction | null;
  /** "I'm still using this" — resets the app's inactivity clock. */
  extend: () => Promise<void>;
}

/**
 * Every action refreshes the app-detail read and the apps list: both render the
 * state these change — the detail page's banner and the list's status column —
 * and a stale row invites a second click on an app already dealt with.
 */
export function useAppActivityActions(input: {
  appId: string;
  appName: string;
}): AppActivityActions {
  const { appId, appName } = input;
  const queryClient = useQueryClient();
  const extendMutation = api.apps.lifecycleExtend.useMutation();
  const [pending, setPending] = React.useState<ActivityAction | null>(null);

  const refresh = React.useCallback(
    () => refreshAfterLifecycleChange(queryClient, appId),
    [appId, queryClient],
  );

  const run = async (
    action: ActivityAction,
    call: () => Promise<{ status: number }>,
    onSuccess: () => void,
    failure: string,
  ): Promise<boolean> => {
    if (pending !== null) return false;
    setPending(action);
    try {
      const res = await call();
      if (res.status >= 300) throw res;
      onSuccess();
      await refresh();
      return true;
    } catch (err) {
      toast.error(`${failure} ${mutationErrorMessage(err)}`);
      return false;
    } finally {
      setPending(null);
    }
  };

  return {
    pending,
    extend: async () => {
      await run(
        'extend',
        () => extendMutation.mutateAsync({ params: { id: appId } }),
        () => toast.success(`${appName} is marked as still needed.`),
        'Could not confirm the app.',
      );
    },
  };
}
